import { DataSource } from '@angular/cdk/table'
import { Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'
import { AsiakkaanTuote } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { KayttajaService } from '../../_angular/service/kayttaja.service'

interface Hakutiedot {
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

@Injectable()
export class TuotteetFirestoreDataSource extends DataSource<AsiakkaanTuote> {
  private _hakutiedot = this._annaOletusHakutiedot()
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  private _kaikkiTuotteetObservable: Observable<AsiakkaanTuote[]>
  asiakkaanTuotteetObservable: Observable<AsiakkaanTuote[]>

  constructor(
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kayttajaService.kayttajanTiedotObservable.pipe(filter(kayttaja => !kayttaja)).subscribe(() => {
      this._hakutiedot = this._annaOletusHakutiedot()
      this._hakutiedotSubject.next(this._hakutiedot)
    })

    this._kaikkiTuotteetObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<AsiakkaanTuote[]>([])
        }
        const uri = this._laskuUriService.getLaskuTuotteetCollectionUri(kayttaja.asiakasAvain)
        return this._firebaseLemonaid
          .firestoreCollection<AsiakkaanTuote>(uri)
          .listenSnapshots()
          .pipe(
            map(snapshots =>
              snapshots.map(snapshot => {
                const tuote = snapshot.data()
                tuote.$key = snapshot.id
                return tuote
              }),
            ),
          )
      }),
      tap(() => {
        setTimeout(() => {
          this._lataaSubject.next(false)
        }, 0)
      }),
      lemonShare(),
    )

    this.asiakkaanTuotteetObservable = combineLatest([this._kaikkiTuotteetObservable, this._hakutiedotSubject, this.lataaObservable]).pipe(
      map(([tuotteet, hakutiedot, lataa]) => {
        if (lataa) {
          return []
        }
        const jarjestetyt = this._suodataJaLajittele(tuotteet, hakutiedot.lajittelutiedot)
        this._rivienMaaraSubject.next(jarjestetyt.length)
        return this._annaSivu(jarjestetyt, hakutiedot.sivutustiedot)
      }),
      lemonShare(),
    )
  }

  private _suodataJaLajittele(tuotteet: AsiakkaanTuote[], lajittelutiedot: Sort): AsiakkaanTuote[] {
    const naytettavat = tuotteet.filter(tuote => !tuote.poistettu)
    const { active, direction } = lajittelutiedot
    if (!active || !direction) {
      return naytettavat
    }
    const suunta = direction === 'asc' ? 1 : -1
    return naytettavat.sort((a, b) => this._vertaaKentat(a, b, active) * suunta)
  }

  private _annaSivu(tuotteet: AsiakkaanTuote[], sivutustiedot: PageEvent): AsiakkaanTuote[] {
    const alku = sivutustiedot.pageIndex * sivutustiedot.pageSize
    return tuotteet.slice(alku, alku + sivutustiedot.pageSize)
  }

  private _vertaaKentat(a: AsiakkaanTuote, b: AsiakkaanTuote, sarake: string): number {
    switch (sarake) {
      case 'hinta':
        return (a.hinta ?? 0) - (b.hinta ?? 0)
      case 'nimi':
        return (a.nimi ?? '').toLowerCase().localeCompare((b.nimi ?? '').toLowerCase())
      default:
        return 0
    }
  }

  private _annaOletusHakutiedot(): Hakutiedot {
    return {
      sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
      lajittelutiedot: { active: 'nimi', direction: 'asc' },
    }
  }

  getSort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  getPage(): PageEvent {
    return this._hakutiedot.sivutustiedot
  }

  changeSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  changePage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  connect(): Observable<AsiakkaanTuote[]> {
    return this.asiakkaanTuotteetObservable
  }

  disconnect() {}
}
